import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchIdeas, fetchAllIdeas, createIdea, type WebsiteIdea } from "../content/ideas";
import { FolderSection } from "../components/future/FolderSection";
import { IdeasTable } from "../components/future/IdeasTable";
import { SubmitDocumentForm } from "../components/common/SubmitDocumentForm";

const PRIORITY_ORDER: Record<WebsiteIdea["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

interface FolderGroup {
  folder: string;
  ideas: WebsiteIdea[];
}

function FolderNav({ groups }: { groups: FolderGroup[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="lg:w-64 flex-shrink-0">
      <div className="lg:sticky lg:top-20">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden w-full flex items-center justify-between px-4 py-2 bg-dark-card border border-dark-border rounded-lg text-warm-100"
        >
          <span className="font-medium">{t('common.jumpToFolder')}</span>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <nav className={`${isOpen ? "block" : "hidden"} lg:block mt-4 lg:mt-0`}>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-warm-500 uppercase tracking-wider mb-3">
              {t('common.foldersHeading')}
            </h3>
            <ul className="space-y-1">
              {groups.map((group) => {
                const anchorId = group.folder ? `folder-${group.folder}` : "folder-root";
                const displayName = group.folder
                  ? group.folder.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                  : t('common.folderGeneral');

                return (
                  <li key={anchorId}>
                    <a
                      href={`#${anchorId}`}
                      className="flex items-center justify-between px-2 py-1.5 rounded text-sm text-warm-200 hover:text-warm-50 hover:bg-dark-hover transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{displayName}</span>
                      <span className="text-xs text-warm-500">{group.ideas.length}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}

export function Future() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [ideas, setIdeas] = useState<WebsiteIdea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(searchParams.get("view") === "all");
  const [allIdeas, setAllIdeas] = useState<WebsiteIdea[]>([]);
  const [allIdeasLoading, setAllIdeasLoading] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetchIdeas = useCallback(() => {
    setFetchKey((k) => k + 1);
    setAllIdeas([]);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchIdeas()
      .then((response) => {
        setIdeas(response.ideas);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('future.failedToLoad'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchKey, t]);

  useEffect(() => {
    if (showTable && allIdeas.length === 0 && !allIdeasLoading) {
      setAllIdeasLoading(true);
      fetchAllIdeas()
        .then((response) => {
          setAllIdeas(response.ideas);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : t('future.failedToLoadAll'));
        })
        .finally(() => {
          setAllIdeasLoading(false);
        });
    }
  }, [showTable, allIdeas.length, allIdeasLoading, t]);

  const groups = useMemo(() => {
    const folderMap = new Map<string, WebsiteIdea[]>();

    for (const idea of ideas) {
      const folder = idea.folder;
      const existing = folderMap.get(folder);
      if (existing) {
        existing.push(idea);
      } else {
        folderMap.set(folder, [idea]);
      }
    }

    const sorted: FolderGroup[] = Array.from(folderMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([folder, folderIdeas]) => ({
        folder,
        ideas: folderIdeas.sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]),
      }));

    return sorted;
  }, [ideas]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t('future.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t('future.failedToLoadWithError', { error })}</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: "#1a1816" }}>
      {/* Ambient radial gradients */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] opacity-[0.07]"
        style={{
          background:
            "radial-gradient(ellipse at center, #EC4899 0%, #8B5CF6 40%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[500px] -left-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, #F97316 0%, #EC4899 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[400px] -right-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, #3B82F6 0%, #06B6D4 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/3 w-[600px] h-[400px] opacity-[0.03]"
        style={{
          background:
            "radial-gradient(ellipse at center, #8B5CF6 0%, #3B82F6 50%, transparent 70%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-warm-50 mb-3">{t('future.title')}</h1>
          <p className="text-warm-400 text-lg">
            {t('future.subtitle')}
          </p>
        </header>

        <SubmitDocumentForm
          type="idea"
          buttonLabel={t('future.submitIdea')}
          titlePlaceholder={t('future.titlePlaceholder')}
          bodyPlaceholder={t('future.bodyPlaceholder')}
          accentColor="amber"
          onSubmit={createIdea}
          onSuccess={refetchIdeas}
        />

        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => setShowTable(false)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              !showTable
                ? "bg-warm-500/20 text-warm-200 border border-warm-500/30"
                : "text-warm-500 hover:text-warm-300"
            }`}
          >
            {t('future.viewCards')}
          </button>
          <button
            onClick={() => setShowTable(true)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              showTable
                ? "bg-warm-500/20 text-warm-200 border border-warm-500/30"
                : "text-warm-500 hover:text-warm-300"
            }`}
          >
            {t('future.viewAllIdeas')}
          </button>
        </div>

        {showTable ? (
          allIdeasLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-warm-400">{t('future.loadingAll')}</p>
            </div>
          ) : (
            <IdeasTable ideas={allIdeas} />
          )
        ) : (
          <div className="lg:flex lg:gap-8">
            <div className="flex-1 min-w-0">
              <div className="space-y-8">
                {groups.map((group) => (
                  <FolderSection key={group.folder} folder={group.folder} ideas={group.ideas} />
                ))}
              </div>

              {groups.length === 0 && (
                <p className="text-warm-500 text-center py-12">{t('future.noIdeas')}</p>
              )}
            </div>

            {groups.length > 0 && <FolderNav groups={groups} />}
          </div>
        )}
      </div>
    </div>
  );
}
